// Posts Table Component
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ExternalLink } from "lucide-react";
import { translations, getPostUrl } from "@/i18n/translations";
import { formatNumber, getPlatformBadgeClass, isChinesePlatform, hasNoViewsData } from "./constants";

const PostsTable = ({ posts, loading, lang = "en", accounts = [] }) => {
  const t = translations[lang] || translations.en;
  
  const getUsername = (accountId) => {
    const account = accounts.find(a => a.id === accountId);
    return account?.username || "";
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="skeleton h-12 rounded-md" />
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Mobile: Card Layout */}
      <div className="md:hidden">
        <div className="h-[400px] overflow-y-auto space-y-2 pr-1">
            {posts.map((post) => {
              const postUrl = post.post_url || getPostUrl(post.platform, post.post_id, getUsername(post.account_id));
              const isChinese = isChinesePlatform(post.platform);
              const showViewsNA = hasNoViewsData(post.platform) && post.views === 0;
              return (
                <div key={post.id} className="p-3 rounded-lg border border-border/30 hover:border-border/60 transition-colors space-y-2" data-testid={`post-row-${post.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{post.title || "Untitled"}</p>
                      <Badge variant="outline" className={`${getPlatformBadgeClass(post.platform)} text-xs capitalize mt-1`}>
                        {post.platform}
                      </Badge>
                    </div>
                    {postUrl && (
                      <a href={postUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted text-primary shrink-0" title={t.posts.openOriginal} data-testid={`post-link-${post.id}`}>
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">{t.posts.viewsHeader}</span>
                      <span className="font-mono font-medium">{showViewsNA ? "N/A" : formatNumber(post.views)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">{t.posts.likesHeader}</span>
                      <span className={`font-mono font-medium ${isChinese ? "text-emerald-500" : ""}`}>{formatNumber(post.likes)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">{t.posts.commentsHeader}</span>
                      <span className="font-mono font-medium">{formatNumber(post.comments)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">{t.posts.engagementHeader}</span>
                      <span className={`font-mono font-medium ${post.engagement_rate > 5 ? "text-emerald-500" : post.engagement_rate > 2 ? "text-yellow-500" : ""}`}>
                        {post.engagement_rate?.toFixed(2) || "0.00"}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Desktop: Table Layout */}
      <div className="hidden md:block">
        <ScrollArea className="h-[400px]">
          <Table>
            <TableHeader>
              <TableRow className="border-border/50 hover:bg-transparent">
                <TableHead className="text-muted-foreground font-mono text-xs uppercase">{t.posts.post}</TableHead>
                <TableHead className="text-muted-foreground font-mono text-xs uppercase">{t.posts.platform}</TableHead>
                <TableHead className="text-right text-muted-foreground font-mono text-xs uppercase">{t.posts.viewsHeader}</TableHead>
                <TableHead className="text-right text-muted-foreground font-mono text-xs uppercase">{t.posts.likesHeader}</TableHead>
                <TableHead className="text-right text-muted-foreground font-mono text-xs uppercase">{t.posts.commentsHeader}</TableHead>
                <TableHead className="text-right text-muted-foreground font-mono text-xs uppercase">{t.posts.engagementHeader}</TableHead>
                <TableHead className="text-center text-muted-foreground font-mono text-xs uppercase">{t.posts.linkHeader}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts.map((post) => {
                const postUrl = post.post_url || getPostUrl(post.platform, post.post_id, getUsername(post.account_id));
                const isChinese = isChinesePlatform(post.platform);
                const showViewsNA = hasNoViewsData(post.platform) && post.views === 0;
                return (
                  <TableRow key={post.id} className="table-row-hover border-border/30" data-testid={`post-row-${post.id}`}>
                    <TableCell className="font-medium max-w-[200px] truncate">{post.title || "Untitled"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${getPlatformBadgeClass(post.platform)} text-xs capitalize`}>
                        {post.platform}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {showViewsNA ? (
                        <span className="text-muted-foreground text-xs">N/A</span>
                      ) : (
                        formatNumber(post.views)
                      )}
                    </TableCell>
                    <TableCell className={`text-right font-mono ${isChinese ? "text-emerald-500 font-semibold" : ""}`}>
                      {formatNumber(post.likes)}
                    </TableCell>
                    <TableCell className="text-right font-mono">{formatNumber(post.comments)}</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono ${post.engagement_rate > 5 ? "text-emerald-500" : post.engagement_rate > 2 ? "text-yellow-500" : "text-muted-foreground"}`}>
                        {post.engagement_rate?.toFixed(2) || "0.00"}%
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {postUrl ? (
                        <a
                          href={postUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center p-1.5 rounded-md hover:bg-muted transition-colors text-primary hover:text-primary/80"
                          title={t.posts.openOriginal}
                          data-testid={`post-link-${post.id}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>
    </>
  );
};

export default PostsTable;
